<template>
  <div class="mdc" :style="{ height: height + 'px' }">
    <div class="mdc-toolbar">
      <span class="mdc-readout" v-if="readout">
        <span class="mdc-sym">{{ ticker }}</span>
        <span class="mdc-dim">1D · {{ readout.when }}</span>
        <span>O <b :class="readout.cls">{{ readout.o }}</b></span>
        <span>H <b :class="readout.cls">{{ readout.h }}</b></span>
        <span>L <b :class="readout.cls">{{ readout.l }}</b></span>
        <span>C <b :class="readout.cls">{{ readout.c }}</b></span>
        <span :class="readout.cls">{{ readout.chg }}</span>
        <span>V <b>{{ readout.v }}</b></span>
        <span v-for="m in readout.mas" :key="m.slot" :style="{ color: m.color }">{{ m.label }} {{ m.value }}</span>
      </span>
    </div>
    <div class="mdc-toolbar mdc-toolbar-toggles">
      <span class="mdc-toggles">
        <label :style="{ color: WEEK_COLOR }"><input type="checkbox" v-model="showWeek" /> weekly</label>
        <label :style="{ color: MONTH_COLOR }"><input type="checkbox" v-model="showMonth" /> monthly</label>
        <label><input type="checkbox" v-model="showBands" /> zones</label>
        <label><input type="checkbox" v-model="showMinor" /> R1 R2 S1 S2</label>
        <label v-for="(a, i) in averageSeries" :key="'ma' + i" :style="{ color: a.color }"
               :title="`Show ${a.label}; the period box reloads the chart`">
          <input type="checkbox" v-model="averageVisible[i]" @change="saveAverageVisible" />
          {{ a.kind.toUpperCase() }}
          <input type="number" class="mdc-period" min="1" :max="MAX_AVERAGE_PERIOD" step="1"
                 :value="a.period" @change="setPeriod(i, $event)" @keyup.enter="$event.target.blur()" />
        </label>
        <label><input type="checkbox" v-model="showVolume" /> volume</label>
        <label><input type="checkbox" v-model="showMarkers" /> watch list</label>
        <label><input type="checkbox" v-model="showStochastic" /> stoch</label>
        <label><input type="checkbox" v-model="showAtr" /> ATR</label>
      </span>
    </div>
    <div class="mdc-info">
      <span v-for="p in periodLines" :key="p.label" :style="{ color: p.color }">{{ p.label }}: {{ p.text }}</span>
    </div>
    <div class="mdc-info mdc-listed">{{ listedLine || '\u00a0' }}</div>
    <div ref="canvas" class="mdc-canvas" @mouseenter="pointerIn = 'main'" @mouseleave="leave('main')"></div>
    <div v-for="p in PANES" v-show="paneShown(p.key)" :key="p.key" class="mdc-pane"
         :style="{ height: PANE_HEIGHT + 'px' }" @mouseenter="pointerIn = p.key" @mouseleave="leave(p.key)">
      <div :ref="'pane-' + p.key" class="mdc-pane-canvas"></div>
      <div class="mdc-pane-legend">
        <span class="mdc-dim">{{ p.key === 'atr' ? `ATR ${atrPeriod}` : p.title }}</span>
        <span v-for="l in paneLegend(p.key)" :key="l.label" :style="{ color: l.color }">{{ l.label }} {{ l.value }}</span>
      </div>
    </div>
    <div v-if="!candles.length" class="mdc-empty">No daily bars for this range.</div>
  </div>
</template>

<script>
/**
 * The macro chart: daily bars with the researcher's view of them.
 *
 * - Weekly and monthly Camarilla ladders (from /api/macro_chart, computed by
 *   shared/common/camarilla_periods.py: the previous completed week / month and
 *   this one's first open, P = (O + H + L + C) / 4), each painted by its own
 *   LadderPrimitive across the sessions of its period, so the levels step at
 *   each week / month boundary. Weekly solid with its zones, monthly dashed.
 * - The trend-continuation researcher's moving averages (SMA50, SMA9, EMA5,
 *   EMA20, SMA20 by default), each behind a toggle with its own period box; a
 *   new period is emitted (`update:averages`) for the view to reload, since the
 *   warm-up lives on the server. Off the price autoscale, as on the post-mortem
 *   chart.
 * - The daily slow stochastic (14, 3, 3) and ATR14, each in its own pane under
 *   the candles: separate charts fed the candles' times as whitespace, the
 *   visible logical range and the crosshair mirrored -- the post-mortem chart's
 *   arrangement (lightweight-charts 4.2 has no panes).
 * - The watch list's decisions, from watchlist_history: an arrow on each
 *   session the ticker was listed (green up, red down, with its confidence), a
 *   grey circle on the bar whose signal it was listed on, and the hovered
 *   session's full entry in the line under the toolbar.
 *
 * Times are the ET session dates ('YYYY-MM-DD', lightweight-charts' business
 * days), so no time-zone shift is needed on a daily chart.
 */
import { createChart, CrosshairMode, LineStyle, PriceScaleMode } from 'lightweight-charts';
import { LadderPrimitive } from '../utils/ladderPrimitive';

const THEME = {
  bg: '#ffffff', text: '#131722', border: '#d1d4dc', up: '#26a69a', down: '#ef5350',
  crosshair: '#9598a1', crosshairLabel: '#131722', evidence: '#9598a1',
};
const WEEK_COLOR = '#1565c0';
const MONTH_COLOR = '#8e24aa';

/** A period ladder's drawn levels; R4/S4 and R3/S3 heavier, R1 R2 S1 S2 behind a toggle. */
function ladderLevels(prefix, colors, dashed, minor) {
  const major = [
    { key: 'r5', name: 'R5', color: colors.rFaint, width: 1 },
    { key: 'r4', name: 'R4', color: colors.r, width: 2 },
    { key: 'r3', name: 'R3', color: colors.r, width: 1 },
    { key: 'p', name: 'P', color: colors.p, width: 1 },
    { key: 's3', name: 'S3', color: colors.s, width: 1 },
    { key: 's4', name: 'S4', color: colors.s, width: 2 },
    { key: 's5', name: 'S5', color: colors.sFaint, width: 1 },
  ];
  const minors = minor ? ['r2', 'r1', 's1', 's2'].map((key) => (
    { key, name: key.toUpperCase(), color: colors.p, width: 1, minor: true })) : [];
  return [...major, ...minors].map((l) => ({
    key: l.key, label: `${prefix}${l.name}`, color: l.color, width: l.width,
    dashed: dashed || !!l.minor, axisColor: l.color.startsWith('rgba') ? colors.axis : l.color,
  }));
}
const WEEK_COLORS = { r: '#26a69a', rFaint: 'rgba(38, 166, 154, 0.6)', s: '#ef5350',
                      sFaint: 'rgba(239, 83, 80, 0.6)', p: 'rgba(120, 123, 134, 0.7)', axis: '#5d606b' };
const MONTH_COLORS = { r: '#8e24aa', rFaint: 'rgba(142, 36, 170, 0.55)', s: '#3949ab',
                       sFaint: 'rgba(57, 73, 171, 0.55)', p: 'rgba(142, 36, 170, 0.45)', axis: '#6a1b9a' };

/** The researcher's averages, one slot each, in drawing order (macro_chart.RESEARCHER_AVERAGES). */
export const DEFAULT_MACRO_AVERAGES = [
  { kind: 'sma', period: 50 },
  { kind: 'sma', period: 9 },
  { kind: 'ema', period: 5 },
  { kind: 'ema', period: 20 },
  { kind: 'sma', period: 20 },
];
const AVERAGE_STYLES = [
  { color: '#ff9800', width: 2 },
  { color: '#2962ff', width: 1 },
  { color: '#e91e63', width: 1 },
  { color: '#00897b', width: 1 },
  { color: '#795548', width: 1 },
];
/** The endpoint's longest period (MAX_AVERAGE_PERIOD in post_mortem_chart.py). */
export const MAX_AVERAGE_PERIOD = 500;
const VISIBLE_KEY = 'macroChart.averageVisible';
const PANES = [
  { key: 'stochastic', title: 'Stoch 14 3 3 (daily)' },
  { key: 'atr', title: 'ATR' },
];
const PANE_HEIGHT = 110;
/** The toolbar rows above the candles: readout, toggles, the period ladders, the watch list. */
const CHROME_HEIGHT = 34 + 30 + 22 + 22;
const SCALE_WIDTH = 72;
const OSC = { k: '#2962ff', d: '#ff6d00', band: '#9598a1', atr: '#5d4037' };
const VOLUME_UP = 'rgba(38, 166, 154, 0.4)';
const VOLUME_DOWN = 'rgba(239, 83, 80, 0.4)';

const fmt = (v) => (Number.isFinite(Number(v)) && v !== null ? Number(v).toFixed(2) : '—');
const fmtVolume = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(Math.round(n));
};
const pretty = (s) => String(s || '').replace(/_/g, ' ');

function loadAverageVisible() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(VISIBLE_KEY));
    if (Array.isArray(stored)) return AVERAGE_STYLES.map((_s, i) => stored[i] !== false);
  } catch (e) { /* unreadable storage: show them all */ }
  return AVERAGE_STYLES.map(() => true);
}

/** [{time, value}] -> {points, byTime}, dropping anything unplottable. */
function prep(points) {
  const out = (points || [])
    .filter((p) => p && p.time && Number.isFinite(Number(p.value)))
    .map((p) => ({ time: p.time, value: Number(p.value) }))
    .sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0));
  return { points: out, byTime: new Map(out.map((p) => [p.time, p.value])) };
}

export default {
  name: 'MacroDailyChart',
  emits: ['update:averages'],
  props: {
    ticker: { type: String, default: '' },
    /** [{time: 'YYYY-MM-DD', open, high, low, close, volume}] */
    candles: { type: Array, default: () => [] },
    /** {week: [{key, start, end, levels}], month: [...]} */
    ladders: { type: Object, default: () => ({}) },
    movingAverages: { type: Object, default: () => ({}) },
    averages: { type: Array, default: () => DEFAULT_MACRO_AVERAGES },
    /** {stochastic: {k, d}} */
    oscillators: { type: Object, default: () => ({}) },
    atr: { type: Array, default: () => [] },
    atrPeriod: { type: Number, default: 14 },
    /** watchlist_history markers from the endpoint. */
    markers: { type: Array, default: () => [] },
    height: { type: Number, default: 720 },
  },
  data() {
    return {
      showWeek: true, showMonth: true, showBands: false, showMinor: false, showVolume: true,
      showMarkers: true, showStochastic: true, showAtr: true, averageVisible: loadAverageVisible(),
      hover: null, pointerIn: null, PANES, PANE_HEIGHT, MAX_AVERAGE_PERIOD, WEEK_COLOR, MONTH_COLOR,
    };
  },
  computed: {
    bars() {
      return (this.candles || []).filter((c) => c && c.time)
        .slice().sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0));
    },
    barByTime() {
      return new Map(this.bars.map((b) => [b.time, b]));
    },
    averageSeries() {
      return AVERAGE_STYLES.map((style, slot) => {
        const spec = (this.averages || [])[slot] || DEFAULT_MACRO_AVERAGES[slot];
        const key = `${spec.kind}${spec.period}`;
        return { ...style, slot, kind: spec.kind, period: spec.period, key,
                 label: `${spec.kind.toUpperCase()}${spec.period}`,
                 ...prep((this.movingAverages || {})[key]) };
      });
    },
    oscillatorSeries() {
      const st = (this.oscillators || {}).stochastic || {};
      return { k: prep(st.k), d: prep(st.d), atr: prep(this.atr) };
    },
    markersByDay() {
      const out = new Map();
      for (const m of this.markers || []) {
        if (!out.has(m.session_date)) out.set(m.session_date, []);
        out.get(m.session_date).push(m);
      }
      return out;
    },
    readout() {
      const bar = this.hover || this.bars[this.bars.length - 1];
      if (!bar) return null;
      const chg = bar.open ? ((bar.close - bar.open) / bar.open) * 100 : 0;
      return {
        when: bar.time,
        o: fmt(bar.open), h: fmt(bar.high), l: fmt(bar.low), c: fmt(bar.close),
        chg: `${chg >= 0 ? '+' : ''}${chg.toFixed(2)}%`,
        v: fmtVolume(bar.volume),
        mas: this.averageSeries.filter((a) => this.averageVisible[a.slot])
          .map((a) => ({ slot: a.slot, label: a.label, color: a.color, value: fmt(a.byTime.get(bar.time)) })),
        cls: bar.close >= bar.open ? 'mdc-up' : 'mdc-down',
      };
    },
    /** The hovered (or last) session's weekly and monthly ladders, the values the lines are drawn at. */
    periodLines() {
      const bar = this.hover || this.bars[this.bars.length - 1];
      if (!bar) return [];
      const keys = ['r5', 'r4', 'r3', ...(this.showMinor ? ['r2', 'r1'] : []), 'p',
                    ...(this.showMinor ? ['s1', 's2'] : []), 's3', 's4', 's5'];
      const l = this.ladders || {};
      return [['Week', l.week, WEEK_COLOR, this.showWeek], ['Month', l.month, MONTH_COLOR, this.showMonth]]
        .filter(([, , , shown]) => shown)
        .map(([name, list, color]) => {
          const lad = (list || []).find((x) => x.start <= bar.time && bar.time <= x.end);
          const text = lad && lad.levels
            ? keys.map((k) => `${k.toUpperCase()} ${fmt(lad.levels[k])}`).join('  ')
            : 'no ladder (no completed prior period in range)';
          return { label: `${name}${lad ? ' ' + lad.key : ''}`, text, color };
        });
    },
    /** The hovered (or last) session's watch-list entries, in full. */
    listedLine() {
      const bar = this.hover || this.bars[this.bars.length - 1];
      const day = bar ? bar.time : null;
      const entries = (day && this.markersByDay.get(day)) || [];
      if (!entries.length) return '';
      return 'Watch list: ' + entries.map((m) => [
        m.direction === 'down' ? '↓ down' : '↑ up', pretty(m.qualifier_type), m.confidence,
        m.signal ? `${pretty(m.signal)}${m.signal_status ? ` (${m.signal_status})` : ''}` : null,
        m.signal_day ? `signal bar ${m.signal_day}` : null,
        Number(m.level) > 0 ? `defend ${fmt(m.level)}` : null, m.macro, m.source,
      ].filter((x) => x !== null && x !== undefined && x !== '').join(' · ')).join(' | ');
    },
  },
  watch: {
    candles() { this.redraw(true); },
    ladders() { this.drawLadders(); },
    showWeek() { this.drawLadders(); },
    showMonth() { this.drawLadders(); },
    showBands() { this.drawLadders(); },
    showMinor() { this.drawLadders(); },
    markers() { this.drawMarkers(); },
    showMarkers() { this.drawMarkers(); },
    movingAverages() { this.drawIndicators(); },
    averages() { this.drawIndicators(); },
    averageVisible: { deep: true, handler() { this.drawIndicators(); } },
    showVolume() { this.drawIndicators(); },
    oscillators() { this.drawPanes(); },
    atr() { this.drawPanes(); },
    showStochastic() { this.layout(); this.drawPanes(); },
    showAtr() { this.layout(); this.drawPanes(); },
  },
  mounted() {
    this.build();
    this.redraw(true);
  },
  beforeUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    for (const p of Object.values(this.panes || {})) p.chart.remove();
    this.panes = {};
    if (this.chart) this.chart.remove();
    this.chart = null;
  },
  methods: {
    chartOptions(el, height, extra = {}) {
      return {
        width: el.clientWidth || (this.$refs.canvas && this.$refs.canvas.clientWidth) || 800,
        height,
        layout: { background: { color: THEME.bg }, textColor: THEME.text, fontSize: 12,
                  fontFamily: '-apple-system, "Segoe UI", Roboto, sans-serif' },
        grid: { vertLines: { visible: false }, horzLines: { visible: false } },
        crosshair: {
          mode: CrosshairMode.Normal,
          vertLine: { color: THEME.crosshair, labelBackgroundColor: THEME.crosshairLabel },
          horzLine: { color: THEME.crosshair, labelBackgroundColor: THEME.crosshairLabel },
        },
        timeScale: { borderColor: THEME.border, rightOffset: 4, barSpacing: 6, minBarSpacing: 1,
                     fixLeftEdge: true },
        ...extra,
      };
    },

    build() {
      const el = this.$refs.canvas;
      this.chart = createChart(el, this.chartOptions(el, Math.max(200, this.height - CHROME_HEIGHT), {
        rightPriceScale: { borderColor: THEME.border, mode: PriceScaleMode.Normal, minimumWidth: SCALE_WIDTH,
                           scaleMargins: { top: 0.08, bottom: 0.2 } },
        handleScale: { axisPressedMouseMove: { time: true, price: true } },
      }));
      this.volumeSeries = this.chart.addHistogramSeries({
        priceScaleId: 'volume', priceFormat: { type: 'volume' }, priceLineVisible: false, lastValueVisible: false,
      });
      this.chart.priceScale('volume').applyOptions({ scaleMargins: { top: 0.84, bottom: 0 }, visible: false });
      this.candleSeries = this.chart.addCandlestickSeries({
        upColor: THEME.up, downColor: THEME.down, borderUpColor: THEME.up, borderDownColor: THEME.down,
        wickUpColor: THEME.up, wickDownColor: THEME.down, priceLineVisible: false,
      });
      // Off the autoscale: a far ladder rung or a slow average would squash the candles.
      this.averageLines = AVERAGE_STYLES.map((a) => this.chart.addLineSeries({
        color: a.color, lineWidth: a.width, priceLineVisible: false, lastValueVisible: false,
        crosshairMarkerVisible: false, autoscaleInfoProvider: () => null,
      }));
      this.monthPrimitive = new LadderPrimitive({ labelRow: 1, boundaries: false, inlineLabels: false });
      this.weekPrimitive = new LadderPrimitive({ labelRow: 0, inlineLabels: false });
      this.candleSeries.attachPrimitive(this.monthPrimitive);
      this.candleSeries.attachPrimitive(this.weekPrimitive);
      this.chart.timeScale().subscribeVisibleLogicalRangeChange(() => this.followLabels());
      this.chart.subscribeCrosshairMove((param) => {
        if (this.pointerIn !== 'main') return;
        this.hover = (param && param.time && this.barByTime.get(this.timeKey(param.time))) || null;
        this.mirrorCrosshair('main', this.hover ? this.hover.time : null);
      });
      this.buildPanes();
      this.syncRanges();
      this.resizeObserver = new ResizeObserver(() => {
        if (this.chart && el.clientWidth) this.chart.applyOptions({ width: el.clientWidth });
        for (const p of Object.values(this.panes)) if (p.el.clientWidth) p.chart.applyOptions({ width: p.el.clientWidth });
      });
      this.resizeObserver.observe(el);
      for (const p of Object.values(this.panes)) this.resizeObserver.observe(p.el);
      this.layout();
    },

    /** lightweight-charts hands business days back as {year, month, day}; our key is 'YYYY-MM-DD'. */
    timeKey(time) {
      if (typeof time === 'string') return time;
      if (time && typeof time === 'object') {
        const pad = (n) => String(n).padStart(2, '0');
        return `${time.year}-${pad(time.month)}-${pad(time.day)}`;
      }
      return null;
    },

    buildPanes() {
      this.panes = {};
      for (const def of PANES) {
        const ref = this.$refs[`pane-${def.key}`];
        const el = Array.isArray(ref) ? ref[0] : ref;
        const chart = createChart(el, this.chartOptions(el, PANE_HEIGHT, {
          layout: { background: { color: THEME.bg }, textColor: THEME.text, fontSize: 11, attributionLogo: false,
                    fontFamily: '-apple-system, "Segoe UI", Roboto, sans-serif' },
          rightPriceScale: { borderColor: THEME.border, minimumWidth: SCALE_WIDTH,
                             scaleMargins: { top: 0.12, bottom: 0.08 } },
          handleScale: { axisPressedMouseMove: { time: true, price: false } },
        }));
        const line = (color, width, extra = {}) => chart.addLineSeries({
          color, lineWidth: width, priceLineVisible: false, lastValueVisible: false,
          crosshairMarkerVisible: false, ...extra,
        });
        const series = {};
        if (def.key === 'stochastic') {
          const fixed = () => ({ priceRange: { minValue: 0, maxValue: 100 } });
          series.k = line(OSC.k, 2, { autoscaleInfoProvider: fixed });
          series.d = line(OSC.d, 1, { autoscaleInfoProvider: fixed });
          for (const price of [80, 20]) {
            series.k.createPriceLine({ price, color: OSC.band, lineWidth: 1, lineStyle: LineStyle.Dashed,
                                       axisLabelVisible: true, title: '' });
          }
        } else {
          series.atr = line(OSC.atr, 2);
        }
        chart.subscribeCrosshairMove((param) => {
          if (this.pointerIn !== def.key) return;
          this.hover = (param && param.time && this.barByTime.get(this.timeKey(param.time))) || null;
          this.mirrorCrosshair(def.key, this.hover ? this.hover.time : null);
        });
        this.panes[def.key] = { chart, series, el };
      }
    },

    syncRanges() {
      const charts = () => [['main', this.chart], ...Object.entries(this.panes).map(([k, p]) => [k, p.chart])];
      let syncing = false;
      for (const [key, chart] of charts()) {
        chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
          if (syncing || !range) return;
          syncing = true;
          for (const [other, c] of charts()) if (other !== key) c.timeScale().setVisibleLogicalRange(range);
          syncing = false;
        });
      }
    },

    mirrorCrosshair(source, time) {
      const o = this.oscillatorSeries;
      const targets = [
        ['main', this.chart, this.candleSeries, (t) => (this.barByTime.get(t) || {}).close],
        ['stochastic', this.panes.stochastic.chart, this.panes.stochastic.series.k, (t) => o.k.byTime.get(t)],
        ['atr', this.panes.atr.chart, this.panes.atr.series.atr, (t) => o.atr.byTime.get(t)],
      ];
      for (const [key, chart, series, valueAt] of targets) {
        if (key === source) continue;
        const value = time != null ? valueAt(time) : null;
        if (value != null && Number.isFinite(value)) chart.setCrosshairPosition(value, time, series);
        else chart.clearCrosshairPosition();
      }
    },

    leave(key) {
      if (this.pointerIn !== key) return;
      this.pointerIn = null;
      this.hover = null;
      this.mirrorCrosshair(key, null);
    },

    paneShown(key) {
      return key === 'stochastic' ? this.showStochastic : this.showAtr;
    },

    layout() {
      if (!this.chart) return;
      const shown = PANES.filter((p) => this.paneShown(p.key));
      this.chart.applyOptions({ height: Math.max(200, this.height - CHROME_HEIGHT - shown.length * PANE_HEIGHT) });
      this.chart.timeScale().applyOptions({ visible: !shown.length });
      const last = shown.length ? shown[shown.length - 1].key : null;
      for (const [key, p] of Object.entries(this.panes)) p.chart.timeScale().applyOptions({ visible: key === last });
      this.$nextTick(() => {
        for (const p of Object.values(this.panes)) if (p.el.clientWidth) p.chart.applyOptions({ width: p.el.clientWidth });
        const range = this.chart && this.chart.timeScale().getVisibleLogicalRange();
        if (range) for (const p of Object.values(this.panes)) p.chart.timeScale().setVisibleLogicalRange(range);
      });
    },

    paneLegend(key) {
      const bar = this.hover || this.bars[this.bars.length - 1];
      const o = this.oscillatorSeries;
      const at = (s) => fmt(bar ? o[s].byTime.get(bar.time) : null);
      if (key === 'stochastic') {
        return [{ label: '%K', value: at('k'), color: OSC.k }, { label: '%D', value: at('d'), color: OSC.d }];
      }
      return [{ label: 'ATR', value: at('atr'), color: OSC.atr }];
    },

    redraw(fit) {
      if (!this.chart) return;
      this.candleSeries.setData(this.bars.map(({ time, open, high, low, close }) => ({ time, open, high, low, close })));
      this.drawLadders();
      this.drawIndicators();
      this.drawPanes();
      this.drawMarkers();
      if (fit && this.bars.length) this.chart.timeScale().fitContent();
    },

    /** One period ladder per primitive: the sessions of the period that are on the chart. */
    periodSessions(list, prefix) {
      const times = this.bars.map((b) => b.time);
      return (list || []).map((lad) => {
        const inside = times.filter((t) => t >= lad.start && t <= lad.end);
        if (!inside.length) return null;
        return { date: `${prefix} ${lad.key}`, firstTime: inside[0], lastTime: inside[inside.length - 1],
                 levels: lad.levels || null };
      }).filter(Boolean);
    },

    drawLadders() {
      if (!this.chart) return;
      const l = this.ladders || {};
      this.weekSessions = this.showWeek ? this.periodSessions(l.week, 'Week') : [];
      this.monthSessions = this.showMonth ? this.periodSessions(l.month, 'Month') : [];
      this.weekPrimitive.setData(this.weekSessions, ladderLevels('W ', WEEK_COLORS, false, this.showMinor),
                                 this.showBands);
      this.monthPrimitive.setData(this.monthSessions, ladderLevels('M ', MONTH_COLORS, true, this.showMinor), false);
      this.followLabels();
    },

    /** Each ladder's axis labels follow the last period in view. */
    followLabels() {
      const range = this.chart && this.chart.timeScale().getVisibleLogicalRange();
      if (!range || !this.bars.length) return;
      const idx = Math.min(this.bars.length - 1, Math.max(0, Math.floor(range.to)));
      const last = this.bars[idx].time;
      for (const [primitive, sessions] of [[this.weekPrimitive, this.weekSessions], [this.monthPrimitive, this.monthSessions]]) {
        const inView = (sessions || []).filter((s) => s.firstTime <= last);
        if (inView.length) primitive.setLabelSession(inView[inView.length - 1].date);
      }
    },

    setPeriod(slot, event) {
      const period = Number(event.target.value);
      const current = this.averageSeries[slot];
      if (!Number.isInteger(period) || period < 1 || period > MAX_AVERAGE_PERIOD) {
        event.target.value = current.period;
        return;
      }
      if (period === current.period) return;
      const next = this.averageSeries.map((a) => ({ kind: a.kind, period: a.period }));
      next[slot] = { kind: current.kind, period };
      this.$emit('update:averages', next);
    },

    saveAverageVisible() {
      try { window.localStorage.setItem(VISIBLE_KEY, JSON.stringify(this.averageVisible)); } catch (e) { /* per-viewer nicety */ }
    },

    drawIndicators() {
      if (!this.chart) return;
      this.averageSeries.forEach((a, i) => this.averageLines[i].setData(this.averageVisible[i] ? a.points : []));
      this.volumeSeries.setData(this.showVolume
        ? this.bars.filter((b) => Number.isFinite(Number(b.volume))).map((b) => ({
          time: b.time, value: Number(b.volume), color: b.close >= b.open ? VOLUME_UP : VOLUME_DOWN,
        }))
        : []);
    },

    /** Every pane gets the candles' times as whitespace, so its logical indexes are the candles'. */
    drawPanes() {
      if (!this.panes) return;
      const times = this.bars.map((b) => b.time);
      const fill = (series, on) => times.map((time) => {
        const value = on ? series.byTime.get(time) : null;
        return value == null ? { time } : { time, value };
      });
      const o = this.oscillatorSeries;
      this.panes.stochastic.series.k.setData(fill(o.k, this.showStochastic));
      this.panes.stochastic.series.d.setData(fill(o.d, this.showStochastic));
      this.panes.atr.series.atr.setData(fill(o.atr, this.showAtr));
      const range = this.chart && this.chart.timeScale().getVisibleLogicalRange();
      if (range) for (const p of Object.values(this.panes)) p.chart.timeScale().setVisibleLogicalRange(range);
    },

    /** The watch list's decisions: an arrow on each listed session, a circle on its signal bar. */
    drawMarkers() {
      if (!this.chart) return;
      const out = [];
      if (this.showMarkers) {
        for (const m of this.markers || []) {
          const down = m.direction === 'down';
          if (this.barByTime.has(m.session_date)) {
            out.push({ time: m.session_date, position: down ? 'aboveBar' : 'belowBar',
                       color: down ? THEME.down : THEME.up, shape: down ? 'arrowDown' : 'arrowUp',
                       text: `${down ? '↓' : '↑'}${m.confidence != null ? m.confidence : ''}` });
          }
          if (m.signal_day && this.barByTime.has(m.signal_day) && m.signal_day !== m.session_date) {
            out.push({ time: m.signal_day, position: down ? 'belowBar' : 'aboveBar', color: THEME.evidence,
                       shape: 'circle', text: pretty(m.signal) });
          }
        }
      }
      const seen = new Set();
      const unique = out.filter((m) => {
        const key = `${m.time}|${m.shape}|${m.text}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      this.candleSeries.setMarkers(unique.sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0)));
    },
  },
};
</script>

<style scoped>
.mdc { position: relative; background: #ffffff; border: 1px solid #d1d4dc; border-radius: 6px; display: flex; flex-direction: column; overflow: hidden; }
.mdc-toolbar { flex: none; display: flex; justify-content: space-between; align-items: center; gap: 1rem;
               padding: 6px 12px; height: 34px; color: #131722; font-size: 12px;
               border-bottom: 1px solid #e0e3eb; font-variant-numeric: tabular-nums; }
.mdc-readout { display: flex; gap: 0.9rem; align-items: baseline; white-space: nowrap; overflow: hidden; }
.mdc-sym { font-weight: 600; font-size: 13px; }
.mdc-dim { color: #787b86; }
.mdc-up { color: #26a69a; }
.mdc-down { color: #ef5350; }
.mdc-toggles { display: flex; gap: 0.8rem; white-space: nowrap; overflow-x: auto; }
.mdc-toggles label { display: flex; gap: 0.3rem; align-items: center; cursor: pointer; margin: 0; }
.mdc-period { width: 3.2rem; height: 20px; padding: 0 2px; font-size: 12px; color: #131722;
              border: 1px solid #d1d4dc; border-radius: 3px; font-variant-numeric: tabular-nums; }
.mdc-toolbar-toggles { height: 30px; justify-content: flex-end; }
.mdc-info { display: flex; gap: 1.5rem; padding: 0 12px; height: 22px; line-height: 21px; font-size: 12px;
            color: #434651; border-bottom: 1px solid #e0e3eb; white-space: nowrap; overflow: hidden;
            text-overflow: ellipsis; font-variant-numeric: tabular-nums; flex: none; }
.mdc-canvas { flex: 1; min-height: 0; position: relative; }
.mdc-pane { position: relative; flex: none; border-top: 1px solid #e0e3eb; }
.mdc-pane-canvas { position: absolute; inset: 0; }
.mdc-pane-legend { position: absolute; top: 3px; left: 8px; z-index: 2; display: flex; gap: 0.8rem;
                   font-size: 11px; pointer-events: none; font-variant-numeric: tabular-nums;
                   background: rgba(255, 255, 255, 0.8); padding: 0 4px; border-radius: 3px; }
.mdc-empty { position: absolute; margin: 4rem auto; width: 100%; text-align: center; color: #787b86; }
</style>
